'use client';

import * as d3 from 'd3';
import { format } from 'date-fns-jalali';
import { motion } from 'framer-motion';
import type { MouseEvent, TouchEvent } from 'react';
import styled from 'styled-components';

import { cssVar, theme, Typography } from '@zoomit/zoomit-ds';

type Props = {
  id: string;
  width: number;
  height: number;
  data: any[]; //TODO: fix any type
  xAccessor(d: any): number;
  yAccessor(d: any): number;
  marginValues?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
};

const Chart = (props: Props) => {
  const { id, width, height, data, xAccessor, yAccessor, marginValues } = props;

  const margin = {
    top: 8,
    right: 4,
    bottom: 24,
    left: 4,
    ...marginValues,
  };

  const tooltipHalfHeight = 36;
  const circleDiameter = 10;

  const xScale = d3
    .scaleTime()
    .domain([xAccessor(data[0]), xAccessor(data[data.length - 1])])
    .range([margin.left, width - margin.right]);

  const yScale = d3
    .scaleLinear()
    .domain(d3.extent(data.map(yAccessor)) as any)
    .range([height - margin.bottom, margin.top])
    .nice();

  const line = d3
    .line()
    .curve(d3.curveCatmullRom)
    .x((d) => xScale(xAccessor(d)))
    .y((d) => yScale(yAccessor(d)));

  const d = line(data) as string;

  const convertTimeStampToDate = (time: number) => {
    return format(time, 'd MMMM yyyy');
  };

  const onPointerMovement = (e: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>) => {
    try {
      const bisect = d3.bisector(xAccessor).left;
      const [x, y] = d3.pointer(e);
      const activeIndex = bisect(data, xScale.invert(x));
      const _d = data[activeIndex];
      const _xPos = xScale(xAccessor(_d));
      const _yPos = yScale(yAccessor(_d));

      // Transform circle
      d3.select(`.circle-${id}`).style('opacity', 1).attr('transform', `translate(${_xPos},${_yPos})`);

      // Set Tooltip content
      d3.select(`.tooltip-area__label-${id}`).text(convertTimeStampToDate(xAccessor(_d)));
      d3.select(`.tooltip-area__value-${id}`).text(
        new Intl.NumberFormat('ir-IR').format(yAccessor(_d)) + ' تومان',
      );

      // Set Tooltip position
      const tooltip = d3.select(`.tooltip-area-${id}`);
      const tooltipArrow = d3.select(`.tooltip-area__arrow-${id}`);
      tooltip.style('opacity', 1).style('top', `${_yPos - tooltipHalfHeight}px`);
      if (x < width / 2) {
        tooltip.style('left', `${_xPos + circleDiameter}px`);
        tooltipArrow
          .style('right', '100%')
          .style('left', 'unset')
          .style('border-right-width', '7px')
          .style('border-left-width', '0px');
      } else {
        tooltip.style(
          'left',
          `${_xPos - (tooltip.node() as HTMLElement).getBoundingClientRect().width - 10}px`,
        );
        tooltipArrow
          .style('left', '100%')
          .style('right', 'unset')
          .style('border-left-width', '7px')
          .style('border-right-width', '0px');
      }
    } catch (error) {
      // catch error
    }
  };

  const onMouseLeave = () => {
    d3.select(`.circle-${id}`).style('opacity', 0);
    d3.select(`.tooltip-area-${id}`).style('opacity', 0);
  };

  return (
    <Root
      height={height}
      onMouseMove={onPointerMovement}
      onTouchMove={onPointerMovement}
      onMouseLeave={onMouseLeave}
    >
      <svg viewBox={`0 0 ${width} ${height}`}>
        {/* Circle */}
        {data.length === 1 && (
          <motion.circle
            r="2"
            fill="currentColor"
            initial={{ opacity: 0, x: xScale(xAccessor(data[0])), y: height }}
            animate={{
              opacity: 1,
              x: xScale(xAccessor(data[0])),
              y: yScale(yAccessor(data[0])),
            }}
            transition={{ duration: 1, type: 'spring' }}
          />
        )}
        {/* Line */}
        <g width={width} height={height - margin.bottom}>
          <motion.path
            d={d}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 2, type: 'spring' }}
          />
        </g>

        {/* X axis */}
        <Text
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 2, type: 'spring' }}
          x={xScale(xAccessor(data[0])) + 32}
          y={height - 4}
          textAnchor="middle"
          className={'fa'}
        >
          {convertTimeStampToDate(xAccessor(data[0]))}
        </Text>
        <Text
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ type: 'spring', delay: 0.5 }}
          x={xScale(xAccessor(data[data.length - 1])) - 32}
          y={height - 4}
          textAnchor="middle"
          className={'fa'}
        >
          {convertTimeStampToDate(xAccessor(data[data.length - 1]))}
        </Text>

        <g className={`circle-${id}`} style={{ opacity: 0 }}>
          <circle r="4" cx={xScale(xAccessor(d))} cy={yScale(yAccessor(d))} fill="currentColor" />
        </g>
      </svg>
      <TooltipContainer className={`tooltip-area-${id}`}>
        <Typography className={`tooltip-area__label-${id} fa`} textColor={cssVar(theme.white)} />
        <Typography
          className={`tooltip-area__value-${id} fa`}
          textColor={cssVar(theme.white)}
          tag="span"
          fontSize={1.4}
          fontWeight="bold"
        />
        <Arrow className={`tooltip-area__arrow-${id}`} />
      </TooltipContainer>
    </Root>
  );
};

export default Chart;

const Root = styled.div<{ height: number }>`
  position: relative;
  width: 100%;
  height: ${({ height }) => height}px;
  color: var(${theme.info});
`;

const Text = styled(motion.text)`
  font-size: 1.2rem;
  line-height: 1.6;
  fill: var(${theme.grey1});
`;

const TooltipContainer = styled(motion.div)`
  border-radius: var(${theme.radius.md});
  padding: 1.2rem;
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  flex-direction: column;
  width: fit-content;
  height: 7.2rem;
  opacity: 0;
  left: 0;
  top: 0;
  transition: 0.125s all;
  transition-delay: 0.01s;

  [data-theme='light'] & {
    background: rgba(25, 25, 35, 0.9);
  }

  [data-theme='dark'] & {
    background-color: var(${theme.card});
  }
`;

const Arrow = styled.div`
  position: absolute;
  top: 50%;
  transform: translate(0, -50%);
  content: '';
  width: 0;
  height: 0;
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-right: 7px solid rgba(25, 25, 35, 0.9);
  border-left: 7px solid rgba(25, 25, 35, 0.9);
`;
